import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';

import {
  defaultButtonsProps,
  defaultTextRender,
  useModals,
} from '../../contexts/ModalContext';

import { ArrowLeftCurvedIcon } from '../../images/shapes';

interface BlockerProps {
  /**
   * Dirty state used for blocking the page from navigating away when the form is dirty.
   */
  dirty?: boolean;
  /**
   * Submitting state used for blocking the page from navigating away when the form is submitting.
   */
  submitting?: boolean;
}

const Blocker = ({ dirty = false, submitting = false }: BlockerProps) => {
  const { t } = useTranslation();
  const modal = useModals();
  const shouldBlock = dirty || submitting;

  const blocker = useBlocker(shouldBlock);

  const handleUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (shouldBlock) {
        event.preventDefault();
      }
    },
    [shouldBlock],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [handleUnload]);

  const showModal = useCallback(async () => {
    if (submitting) {
      blocker?.reset?.();
      await modal({
        title: defaultTextRender.warning(t('DirtyHandler.SaveInProgress')),
        content: t('DirtyHandler.SaveInProgressConfirm'),
        buttons: [{ ...defaultButtonsProps.cancel, label: t('Global.Close') }],
      });
      return;
    }

    const result = await modal({
      title: defaultTextRender.warning(t('DirtyHandler.ShouldLeave')),
      content: t('DirtyHandler.ShouldLeaveConfirm'),
      buttons: [
        {
          ...defaultButtonsProps.save,
          iconImage: <ArrowLeftCurvedIcon className="w-3 min-w-3" />,
          label: t('DirtyHandler.Leave'),
          result: true,
        },
        {
          ...defaultButtonsProps.cancel,
          label: t('DirtyHandler.Stay'),
          result: false,
        },
      ],
    });
    if (result) {
      blocker?.proceed?.();
    } else {
      blocker?.reset?.();
    }
  }, [modal, blocker, submitting, t]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (!shouldBlock) {
        blocker.proceed();
      } else {
        showModal();
      }
    }
  }, [showModal, blocker, shouldBlock]);

  return null;
};

export default Blocker;
